import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";

import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { SettingsProvider } from "./context/SettingsContext";
import { ChatProvider } from "./chat/ChatContext";

import Home from "./pages/Home";


// Dimuat hanya saat rutenya dibuka (code splitting) supaya bundle pembeli kecil.
const Catalog = lazy(() => import("./pages/Catalog"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Panduan = lazy(() => import("./pages/Panduan"));
const PanduanDetail = lazy(() => import("./pages/PanduanDetail"));
const SyaratKetentuan = lazy(() => import("./pages/SyaratKetentuan"));
const HalamanNaskah = lazy(() => import("./pages/HalamanNaskah"));
const Cart = lazy(() => import("./pages/Cart"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const MasukGoogle = lazy(() => import("./pages/MasukGoogle"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderHistory = lazy(() => import("./pages/OrderHistory"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const LacakPengiriman = lazy(() => import("./pages/LacakPengiriman"));
const AkunSaya = lazy(() => import("./pages/AkunSaya"));
const PublicInvoice = lazy(() => import("./pages/PublicInvoice"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminOrderDetail = lazy(() => import("./pages/admin/AdminOrderDetail"));
const AdminLacak = lazy(() => import("./pages/admin/AdminLacak"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminPanduan = lazy(() => import("./pages/admin/AdminPanduan"));
const AdminDiscounts = lazy(() => import("./pages/admin/AdminDiscounts"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminKontenBeranda = lazy(() => import("./pages/admin/AdminKontenBeranda"));
const AdminLaporan = lazy(() => import("./pages/admin/AdminLaporan"));
const AdminPengiriman = lazy(() => import("./pages/admin/AdminPengiriman"));
const AdminChat = lazy(() => import("./pages/admin/AdminChat"));
const ChatHalaman = lazy(() => import("./chat/ChatHalaman"));

import ErrorBoundary from "./components/ErrorBoundary";
import CheckoutErrorFallback from "./components/CheckoutErrorFallback";

function Loader() {
  // PR desain admin (perbaikan jarak, butir 8): di rute /admin pemuat memakai latar gelap admin, bukan latar terang halaman pembeli
  const { pathname } = useLocation();
  if (pathname === "/admin" || pathname.startsWith("/admin/")) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "#010207", color: "#6E7383" }} data-testid="admin-pemuat">Memuat…</div>;
  }
  return <div className="min-h-screen flex items-center justify-center text-stone-400">Memuat…</div>;
}

function RequireAuth({ children, admin = false }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <Loader />;
  // PR chat pembeli: /chat membawa konteks di query, ikut dibawa kembali sesudah masuk
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname === "/chat" ? `${loc.pathname}${loc.search}` : loc.pathname }} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function SeoRouteGuard() {
  const loc = useLocation();
  useEffect(() => {
    const path = loc.pathname;
    let batal = false;
    // Data SEO diambil dari server (lib/seo → /api/seo/data), tidak dibekukan saat build.
    import("./lib/seo").then(({ applySeo, routeSeo, seoData, muatSeo }) => muatSeo().then(() => {
      if (batal) return;
      const privat = path.startsWith("/admin") || path.startsWith("/invoice")
        || (seoData.noindex || []).some((p) => path === p || path.startsWith(p + "/"));
      if (privat) {
        applySeo({ path, robots: "noindex, nofollow" });
        return;
      }
      // Panduan resolves visibility and current SEO from its API, never from a stale build.
      if (path === "/panduan" || path.startsWith("/panduan/")) return;
      const r = routeSeo(path);
      if (r) {
        applySeo({ path, title: r.title, description: r.description, image: r.image, jsonld: r.jsonld });
      }
    }));
    return () => { batal = true; };
  }, [loc.pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/atur-sandi/:token" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/masuk-google" element={<MasukGoogle />} />   {/* PR login Google: tukar kode sekali pakai → sesi */}

      <Route path="/" element={<Home />} />
      <Route path="/katalog" element={<ErrorBoundary pembeli context="Katalog"><Catalog /></ErrorBoundary>} />
      <Route path="/katalog/:kategoriSlug" element={<ErrorBoundary pembeli context="Katalog"><Catalog /></ErrorBoundary>} />
      <Route path="/produk/:id" element={<ProductDetail />} />
      <Route path="/panduan" element={<Panduan />} />
      <Route path="/panduan/:slug" element={<PanduanDetail />} />
      <Route path="/syarat-ketentuan" element={<SyaratKetentuan />} />
      <Route path="/kebijakan-privasi" element={<HalamanNaskah key="privasi" jenis="privasi" />} />
      <Route path="/faq" element={<HalamanNaskah key="faq" jenis="faq" />} />
      <Route path="/invoice/:token" element={<PublicInvoice />} />
      <Route path="/keranjang" element={<RequireAuth><ErrorBoundary pembeli context="Keranjang"><Cart /></ErrorBoundary></RequireAuth>} />
      <Route path="/checkout" element={<RequireAuth><ErrorBoundary pembeli context="Checkout" renderFallback={() => <CheckoutErrorFallback />}><Checkout /></ErrorBoundary></RequireAuth>} />
      <Route path="/pesanan" element={<RequireAuth><OrderHistory /></RequireAuth>} />
      <Route path="/pesanan/:id" element={<RequireAuth><OrderDetail /></RequireAuth>} />
      <Route path="/pesanan/:id/lacak" element={<RequireAuth><LacakPengiriman /></RequireAuth>} />
      <Route path="/akun" element={<RequireAuth><AkunSaya /></RequireAuth>} />
      <Route path="/chat" element={<RequireAuth><ChatHalaman /></RequireAuth>} />

      <Route path="/admin" element={<RequireAuth admin><AdminLayout /></RequireAuth>}>
        <Route index element={<Navigate to="/admin/pesanan" replace />} />
        <Route path="pesanan" element={<AdminOrders />} />
        <Route path="pesanan/:id" element={<AdminOrderDetail />} />
        <Route path="lacak" element={<AdminLacak />} />
        <Route path="chat" element={<AdminChat />} />
        <Route path="chat/:id" element={<AdminChat />} />
        <Route path="laporan" element={<AdminLaporan />} />
        <Route path="produk" element={<AdminProducts />} />
        <Route path="panduan" element={<AdminPanduan />} />
        <Route path="diskon" element={<AdminDiscounts />} />
        <Route path="pelanggan" element={<AdminCustomers />} />
        <Route path="konten-beranda" element={<AdminKontenBeranda />} />
        <Route path="pengiriman" element={<AdminPengiriman />} />
        <Route path="pengaturan" element={<AdminSettings />} />
        {/* PR desain admin: menu lama digabung (Kategori & Riwayat Harga → tab Produk; Email → tab Pengaturan; Log Webhook → Data & Teknis) */}
        <Route path="kategori" element={<Navigate to="/admin/produk?tab=kategori" replace />} />
        <Route path="riwayat-harga" element={<Navigate to="/admin/produk?tab=kategori" replace />} />
        <Route path="email" element={<Navigate to="/admin/pengaturan?tab=email" replace />} />
        <Route path="log-webhook" element={<Navigate to="/admin/pengaturan?tab=data" replace />} />
      </Route>

      <Route path="/pages/*" element={<NotFound gone />} />
      <Route path="/products/*" element={<NotFound gone />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <SettingsProvider>
        <AuthProvider>
          <CartProvider>
            <Toaster position="top-right" richColors />
            <SeoRouteGuard />
            <ChatProvider>
              <Suspense fallback={<Loader />}>
                <AppRoutes />
              </Suspense>
            </ChatProvider>
          </CartProvider>
        </AuthProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}

export default App;
